import React, { useState, useEffect, useRef } from "react";
import "../../../../../css/Recruitment/Sections.css";
import YesNoRadio from "../RadioButton";
import { writeDataS8 } from "../../../../../backend/Recruitment/Sections/Section8/writeDataS8";
import { readDataS8 } from "../../../../../backend/Recruitment/Sections/Section8/readDataS8";
import {
  addSubmissionListener,
  getIsSubmitted,
} from "../../Utils/submissionListener";
import errorTrackerV2 from "../Validation/Utils/ErrorTrackerV2";
import { auth } from "../../../../../backend/firebaseConfig";
import { validateSection8 } from "../Validation/section8";
import InputField from "../../../../DesignSystem/Inputs/InputField";
import AlertBanner from "../../../../DesignSystem/AlertBanner/AlertBanner";
import PrimaryButton from "../../../../DesignSystem/Buttons/PrimaryButton";
import TextArea from "../../../../DesignSystem/Inputs/TextArea";
const defaultRows = {
  loan: { type: "", lender: "", monthlyAmount: "", outstandingAmount: "" },
  dmp: { practitionerName: "", monthlyAmount: "", outstandingAmount: "" },
  fsDebt: { to: "", amount: "" },
  btl: { lender: "", monthlyAmount: "", outstandingAmount: "" },
};

const coerceBool = (v) =>
  v === true || v === false ? v : String(v ?? "").toLowerCase() === "true";

const Section8 = ({ onValidationStatusChange }) => {
  const [formData, setFormData] = useState({
    additionalInformation: "",

    // yes/no (null = unanswered)
    expenditureExceedsIncome: null,
    liabilitiesExceedAssets: null,
    outstandingLiabilities: null,
    propertyRepossessed: null,
    otherProperty: null,
    taxLiabilities: null,

    // income
    currentMonthlyIncome: "",
    partnerMonthlyIncome: "",
    rentalIncome: null,
    rentalIncomeAmount: "",
    otherIncome: null,
    otherIncomeSource: "",
    otherIncomeAmount: "",
    netMonthlyIncome: "",

    // assets & liabilities
    residentialMortgage: null,
    residentialMortgageLender: "",
    residentailMortgageAmount: "",

    residentialHouseValue: "",
    residentialOutstandingAmount: "",

    outstandindLoansOrCreditCards: null,
    householdExpenditurePerMonth: "",

    BTLMortage: null,
    debtManagementPlan: null,
    outstandingFinancialServicesDebt: null,

    // tables
    outstandingLoandsOrCreditCardDetails: [{ ...defaultRows.loan }],
    debtManagementPlanDetails: [{ ...defaultRows.dmp }],
    outstandingFinancialServicesDebtDetails: [{ ...defaultRows.fsDebt }],
    BTLMortageDetails: [{ ...defaultRows.btl }],

    // totals
    totalAssets: "",
    totalLiabilities: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(getIsSubmitted());
  const [showErrors, setShowErrors] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const sectionTopRef = useRef(null);

  // submission listener – stable
  useEffect(() => {
    const remove = addSubmissionListener(setIsSubmitted);
    setIsSubmitted(getIsSubmitted());
    return () => remove();
  }, []);

  const isAnyYesSelected = (data) =>
    [
      "expenditureExceedsIncome",
      "liabilitiesExceedAssets",
      "outstandingLiabilities",
      "propertyRepossessed",
      "otherProperty",
      "taxLiabilities",
      "rentalIncome",
      "otherIncome",
      "residentialMortgage",
      "outstandindLoansOrCreditCards",
      "BTLMortage",
      "debtManagementPlan",
      "outstandingFinancialServicesDebt",
    ].some((k) => data[k] === true);

  // ---------- Handlers

  const handleYesNoChange = (e, fieldName) => {
    const raw = e?.target?.value;
    const bool =
      raw === true || raw === false
        ? raw
        : String(raw).toLowerCase() === "true";
    setFormData((prev) => ({ ...prev, [fieldName]: bool }));
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const parsed =
      type === "number" ? (value === "" ? "" : parseFloat(value)) : value;
    setFormData((prev) => ({ ...prev, [name]: parsed }));
  };

  const handleTableChange = (e, index, field, category) => {
    const { value } = e.target;
    setFormData((prev) => {
      const arr = [...(prev[category] || [])];
      arr[index] = { ...(arr[index] || {}), [field]: value };
      return { ...prev, [category]: arr };
    });
  };

  const handleAddRow = (category) => {
    const templates = {
      outstandingLoandsOrCreditCardDetails: defaultRows.loan,
      debtManagementPlanDetails: defaultRows.dmp,
      outstandingFinancialServicesDebtDetails: defaultRows.fsDebt,
      BTLMortageDetails: defaultRows.btl,
    };
    const newRow = templates[category] || {};
    setFormData((prev) => ({
      ...prev,
      [category]: [...(prev[category] || []), { ...newRow }],
    }));
  };

  const handleRemoveRow = (category) => {
    setFormData((prev) => {
      const arr = [...(prev[category] || [])];
      if (arr.length > 1) arr.pop();
      return { ...prev, [category]: arr };
    });
  };

  useEffect(() => {
    const nextErrors = validateSection8(formData);

    // console.log("Section 8 - Validation Errors:", nextErrors);
    // console.log("Section 8 - showErrors state:", showErrors);

    setErrors((prev) => {
      const prevStr = JSON.stringify(prev);
      const nextStr = JSON.stringify(nextErrors);
      return prevStr === nextStr ? prev : nextErrors;
    });

    const ok = Object.keys(nextErrors).length === 0;

    // tracker should store errors-only
    errorTrackerV2.setSectionErrors("section8", nextErrors);

    // do NOT put onValidationStatusChange in deps unless it's stable
    onValidationStatusChange?.(ok);
  }, [formData]); //  keep deps minimal

  // ---------- Derived totals (avoid re-render loops)
  useEffect(() => {
    const addAmounts = (array, monthlyField, outstandingField) => {
      let a = 0,
        l = 0;
      (array || []).forEach((item) => {
        if (
          monthlyField &&
          item?.[monthlyField] !== "" &&
          item?.[monthlyField] != null
        ) {
          a += parseFloat(item[monthlyField]) || 0;
        }
        if (
          outstandingField &&
          item?.[outstandingField] !== "" &&
          item?.[outstandingField] != null
        ) {
          l += parseFloat(item[outstandingField]) || 0;
        }
      });
      return { a, l };
    };

    const l1 = addAmounts(
      formData.outstandingLoandsOrCreditCardDetails,
      "monthlyAmount",
      "outstandingAmount",
    );
    const l2 = addAmounts(
      formData.debtManagementPlanDetails,
      "monthlyAmount",
      "outstandingAmount",
    );
    const l3 = addAmounts(
      formData.outstandingFinancialServicesDebtDetails,
      null,
      "amount",
    );
    const l4 = addAmounts(
      formData.BTLMortageDetails,
      "monthlyAmount",
      "outstandingAmount",
    );

    const totalAssets = (l1.a + l2.a + l4.a).toString();
    const totalLiabilities = (l1.l + l2.l + l3.l + l4.l).toString();

    setFormData((prev) => {
      if (
        prev.totalAssets === totalAssets &&
        prev.totalLiabilities === totalLiabilities
      )
        return prev;
      return { ...prev, totalAssets, totalLiabilities };
    });
  }, [
    formData.outstandingLoandsOrCreditCardDetails,
    formData.debtManagementPlanDetails,
    formData.outstandingFinancialServicesDebtDetails,
    formData.BTLMortageDetails,
  ]);

  // Net monthly income (avoid loops)
  useEffect(() => {
    const total =
      (parseFloat(formData.currentMonthlyIncome) || 0) +
      (parseFloat(formData.partnerMonthlyIncome) || 0) +
      (formData.rentalIncome
        ? parseFloat(formData.rentalIncomeAmount) || 0
        : 0) +
      (formData.otherIncome ? parseFloat(formData.otherIncomeAmount) || 0 : 0);

    setFormData((prev) => {
      if (prev.netMonthlyIncome === total.toString()) return prev;
      return { ...prev, netMonthlyIncome: total.toString() };
    });
  }, [
    formData.currentMonthlyIncome,
    formData.partnerMonthlyIncome,
    formData.rentalIncomeAmount,
    formData.otherIncomeAmount,
    formData.rentalIncome,
    formData.otherIncome,
  ]);

  // Load Firestore data (normalise + keep defaults)
  useEffect(() => {
    (async () => {
      const data = await readDataS8();
      if (!data) return;

    setFormData((prev) => ({
      ...prev,
      ...data,

      // ✅ backward compatible typo fix
      residentailMortgageAmount:
        data.residentailMortgageAmount ??
        data.residentialMortgageAmount ??
        prev.residentailMortgageAmount,

      // booleans
      expenditureExceedsIncome: coerceBool(
        data.expenditureExceedsIncome ?? prev.expenditureExceedsIncome,
      ),
      liabilitiesExceedAssets: coerceBool(
        data.liabilitiesExceedAssets ?? prev.liabilitiesExceedAssets,
      ),
      outstandingLiabilities: coerceBool(
        data.outstandingLiabilities ?? prev.outstandingLiabilities,
      ),
      propertyRepossessed: coerceBool(
        data.propertyRepossessed ?? prev.propertyRepossessed,
      ),
      otherProperty: coerceBool(data.otherProperty ?? prev.otherProperty),
      taxLiabilities: coerceBool(data.taxLiabilities ?? prev.taxLiabilities),

      rentalIncome: coerceBool(data.rentalIncome ?? prev.rentalIncome),
      otherIncome: coerceBool(data.otherIncome ?? prev.otherIncome),

      residentialMortgage: coerceBool(
        data.residentialMortgage ?? prev.residentialMortgage,
      ),
      outstandindLoansOrCreditCards: coerceBool(
        data.outstandindLoansOrCreditCards ??
          prev.outstandindLoansOrCreditCards,
      ),
      BTLMortage: coerceBool(data.BTLMortage ?? prev.BTLMortage),
      debtManagementPlan: coerceBool(
        data.debtManagementPlan ?? prev.debtManagementPlan,
      ),
      outstandingFinancialServicesDebt: coerceBool(
        data.outstandingFinancialServicesDebt ??
          prev.outstandingFinancialServicesDebt,
      ),

      // arrays
      outstandingLoandsOrCreditCardDetails: data
        .outstandingLoandsOrCreditCardDetails?.length
        ? data.outstandingLoandsOrCreditCardDetails
        : prev.outstandingLoandsOrCreditCardDetails,

      debtManagementPlanDetails: data.debtManagementPlanDetails?.length
        ? data.debtManagementPlanDetails
        : prev.debtManagementPlanDetails,

      outstandingFinancialServicesDebtDetails: data
        .outstandingFinancialServicesDebtDetails?.length
        ? data.outstandingFinancialServicesDebtDetails
        : data.outstandingFianacialServicesDebtDetails?.length
          ? data.outstandingFianacialServicesDebtDetails
          : prev.outstandingFinancialServicesDebtDetails,

      BTLMortageDetails: data.BTLMortageDetails?.length
        ? data.BTLMortageDetails
        : prev.BTLMortageDetails,
    }));

    })();
  }, []);

  // Submit (fixed)
  const handleSubmit = (e) => {
    e.preventDefault();
    setShowErrors(true);
    sectionTopRef.current?.scrollIntoView({ behavior: "smooth" });

    const validationErrors = validateSection8(formData);
    const ok = Object.keys(validationErrors).length === 0;

    // console.log("Section 8 - Submit clicked");
    // console.log("Section 8 - Form Data:", formData);
    // console.log("Section 8 - Validation Errors on Submit:", validationErrors);
    // console.log("Section 8 - Is Valid:", ok);
    // console.log("Section 8 - showErrors set to:", true);

    // Save data regardless of validation status
    writeDataS8(formData);

    // Show appropriate alert based on validation result
    if (ok ) {
      setAlertMessage("Data saved successfully!");
      setAlertType("success");
    } else {
      setAlertMessage(
        "Data saved with errors. Please review the fields marked with errors.",
      );
      setAlertType("alert");
    }
  };

  const isValid = Object.keys(errors).length === 0;

  return (
    <form className="section-form" onSubmit={handleSubmit} ref={sectionTopRef}>
      {alertMessage && (
        <AlertBanner
          type={alertType}
          message={alertMessage}
          onClose={() => setAlertMessage("")}
        />
      )}
      <h3>Section 8 – Overall Financial Position</h3>
      <div className="Content">
        We reserve the right to verify the information given in this section at
        any time and seek evidence where necessary, to discharge our
        responsibilities under the Financial Conduct Authority rules.
      </div>
      <div className="Content">
        We will obtain a credit reference check on every individual prior to
        appointment.
      </div>

      {/* 8.1 */}
      <div className="input-group-row">
        <div className="Content">
          8.1 Does your expenditure exceed your income?
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="expenditureExceedsIncome"
            value={formData.expenditureExceedsIncome}
            error={errors.expenditureExceedsIncome}
            onChange={(e) => handleYesNoChange(e, "expenditureExceedsIncome")}
          />
        </div>
      </div>

      {/* 8.2 */}
      <div className="input-group-row">
        <div className="Content">
          8.2 Do your liabilities exceed your assets?
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="liabilitiesExceedAssets"
            value={formData.liabilitiesExceedAssets}
            error={errors.liabilitiesExceedAssets}
            onChange={(e) => handleYesNoChange(e, "liabilitiesExceedAssets")}
          />
        </div>
      </div>

      {/* 8.3 */}
      <div className="input-group-row">
        <div className="Content">
          8.3 Do you have any outstanding liabilities that are in arrears or
          subject to a specially agreed repayment schedule?
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="outstandingLiabilities"
            value={formData.outstandingLiabilities}
            error={errors.outstandingLiabilities}
            onChange={(e) => handleYesNoChange(e, "outstandingLiabilities")}
          />
        </div>
      </div>

      {/* 8.4 */}
      <div className="input-group-row">
        <div className="Content">
          8.4 Have you ever had any property repossessed, made the subject of a
          repossession order, or voluntarily surrendered?
          <span style={{ color: "var(--Error)" }}> *</span>
          <div className="Content-Small">
            (please provide the address this relates to in the Additional
            Information page at the back of the application)
          </div>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="propertyRepossessed"
            value={formData.propertyRepossessed}
            error={errors.propertyRepossessed}
            onChange={(e) => handleYesNoChange(e, "propertyRepossessed")}
          />
        </div>
      </div>

      {/* 8.5 */}
      <div className="input-group-row">
        <div className="Content">
          8.5 Do you have any properties other than that shown as your current
          address?
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="otherProperties"
            value={formData.otherProperty}
            error={errors.otherProperty}
            onChange={(e) => handleYesNoChange(e, "otherProperty")}
          />
        </div>
      </div>
      <div className="Content-Small">
        If yes, please provide full address(es), including postcode(s), current
        use of property and dates of occupation below.
      </div>

      {/* 8.6 */}
      <div className="input-group-row">
        <div className="Content">
          8.6 Are you up to date with all your individual (and if applicable
          business) tax liabilities? (If no, please provide full details in the
          Additional Information page at the back of the application)
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="taxLiabilities"
            value={formData.taxLiabilities}
            error={errors.taxLiabilities}
            onChange={(e) => handleYesNoChange(e, "taxLiabilities")}
          />
        </div>
      </div>

      {/* Additional info if any yes */}
      {isAnyYesSelected(formData) && (
        <div>
          {/* <div className="sizebox" /> */}
          <div className="Content">Please provide full details below:</div>
          <TextArea
            className="long-textarea"
            isTextArea
            placeholder="Enter your text here..."
            name="additionalInformation"
            value={formData.additionalInformation}
            error={errors.additionalInformation}
            onChange={handleChange}
            multiline
            rows={6}
          />
        </div>
      )}

      <div className="separator" />
      <div className="Content">
        8.7 Financial Status Questionnaire
        <div className="Content-Small">
          Please provide details of all sources of income and liabilities
          (including any business liabilities for which you are or may become
          liable) indicating whether they apply to you individually or jointly.
        </div>
      </div>

      {/* Income */}
      <div className="input-group-row">
        <div className="Content">
          What is your current monthly income (net of tax)? (£)
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <InputField
            name="currentMonthlyIncome"
            type="number"
            placeholder="Monthly Income"
            value={formData.currentMonthlyIncome}
            error={showErrors && !!errors.currentMonthlyIncome}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="input-group-row">
        <div className="Content">
          What is your partner's monthly income (net of tax)? (£)
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <InputField
            name="partnerMonthlyIncome"
            type="number"
            placeholder="Partner's Monthly Income"
            value={formData.partnerMonthlyIncome}
            error={showErrors && !!errors.partnerMonthlyIncome}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="input-group-row">
        <div className="Content">
          Do you have any rental income?
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="rentalIncome"
            value={formData.rentalIncome}
            error={errors.rentalIncome}
            onChange={(e) => handleYesNoChange(e, "rentalIncome")}
          />
        </div>
      </div>

      {formData.rentalIncome === true && (
        <div className="input-group-row">
          <div className="Content"></div>
          <div className="input-group">
            <InputField
              name="rentalIncomeAmount"
              type="number"
              placeholder="Rental Income"
              value={formData.rentalIncomeAmount}
              error={showErrors && !!errors.rentalIncomeAmount}
              onChange={handleChange}
            />
          </div>
        </div>
      )}

      <div className="input-group-row">
        <div className="Content">
          Do you have any other income (e.g. investments/pension)?
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="otherIncome"
            value={formData.otherIncome}
            error={errors.otherIncome}
            onChange={(e) => handleYesNoChange(e, "otherIncome")}
          />
        </div>
      </div>

      {formData.otherIncome === true && (
        <div className="input-group-row">
          <div className="Content"></div>
          <div className="input-group">
            <InputField
              name="otherIncomeSource"
              placeholder="Source"
              value={formData.otherIncomeSource}
              error={showErrors && !!errors.otherIncomeSource}
              onChange={handleChange}
            />
            <InputField
              name="otherIncomeAmount"
              type="number"
              placeholder="Monthly Amount"
              value={formData.otherIncomeAmount}
              error={showErrors && !!errors.otherIncomeAmount}
              onChange={handleChange}
            />
          </div>
        </div>
      )}

      <div className="input-group-row">
        <div className="Content">Total Net Monthly Income (£)</div>
        <div className="input-group">
          <InputField
            name="netMonthlyIncome"
            type="number"
            readOnly
            value={formData.netMonthlyIncome}
          />
        </div>
      </div>

      <div className="separator" />
      <div className="Content">8.8 Assets and Liabilities</div>

      {/* Residential Mortgage */}
      <div className="input-group-row">
        <div className="Content">
          Do you have a residential mortgage?
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="residentialMortgage"
            value={formData.residentialMortgage}
            error={errors.residentialMortgage}
            onChange={(e) => handleYesNoChange(e, "residentialMortgage")}
          />
        </div>
      </div>
      {formData.residentialMortgage === true && (
        <div className="input-group">
          <InputField
            name="residentialMortgageLender"
            placeholder="Lender"
            value={formData.residentialMortgageLender}
            error={showErrors && !!errors.residentialMortgageLender}
            onChange={handleChange}
          />
          <InputField
            name="residentailMortgageAmount"
            type="number"
            placeholder="Monthly Repayment Amount"
            value={formData.residentailMortgageAmount}
            error={showErrors && !!errors.residentailMortgageAmount}
            onChange={handleChange}
          />
          <InputField
            name="residentialHouseValue"
            type="number"
            placeholder="House Value"
            value={formData.residentialHouseValue}
            error={showErrors && !!errors.residentialHouseValue}
            onChange={handleChange}
          />
          <InputField
            name="residentialOutstandingAmount"
            type="number"
            placeholder="Total amount outstanding"
            value={formData.residentialOutstandingAmount}
            error={showErrors && !!errors.residentialOutstandingAmount}
            onChange={handleChange}
          />
        </div>
      )}

      {/* Loans / CC */}
      <div className="input-group-row">
        <div className="Content">
          Do you have any outstanding loans or credit cards?
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="outstandindLoansOrCreditCards"
            value={formData.outstandindLoansOrCreditCards}
            error={errors.outstandindLoansOrCreditCards}
            onChange={(e) =>
              handleYesNoChange(e, "outstandindLoansOrCreditCards")
            }
          />
          {formData.outstandindLoansOrCreditCards === true && (
            <div className="button-group">
              <button
                type="button"
                className="icon-button add-button"
                disabled={!formData.outstandindLoansOrCreditCards}
                onClick={() =>
                  handleAddRow("outstandingLoandsOrCreditCardDetails")
                }
              >
                <i className="fas fa-plus" />
              </button>
              <button
                type="button"
                className="icon-button delete-button"
                disabled={!formData.outstandindLoansOrCreditCards}
                onClick={() =>
                  handleRemoveRow("outstandingLoandsOrCreditCardDetails")
                }
              >
                <i className="fas fa-trash" />
              </button>
            </div>
          )}
        </div>
      </div>

      {formData.outstandindLoansOrCreditCards === true &&
        formData.outstandingLoandsOrCreditCardDetails.map((entry, index) => (
          <div key={index} className="input-group">
            <InputField
              name="loanOrCreditCard"
              placeholder="Loan/Credit Card"
              value={entry.type}
              error={showErrors && !!errors[`loanOrCreditCard_${index}_type`]}
              onChange={(e) =>
                handleTableChange(
                  e,
                  index,
                  "type",
                  "outstandingLoandsOrCreditCardDetails",
                )
              }
            />
            <InputField
              name="lender"
              placeholder="Lender"
              value={entry.lender}
              error={showErrors && !!errors[`loanOrCreditCard_${index}_lender`]}
              onChange={(e) =>
                handleTableChange(
                  e,
                  index,
                  "lender",
                  "outstandingLoandsOrCreditCardDetails",
                )
              }
            />
            <InputField
              name="monthlyAmount"
              type="number"
              placeholder="Monthly Repayment Amount"
              value={entry.monthlyAmount}
              error={
                showErrors &&
                !!errors[`loanOrCreditCard_${index}_monthlyAmount`]
              }
              onChange={(e) =>
                handleTableChange(
                  e,
                  index,
                  "monthlyAmount",
                  "outstandingLoandsOrCreditCardDetails",
                )
              }
            />
            <InputField
              name="outstandingAmount"
              type="number"
              placeholder="Total amount outstanding"
              value={entry.outstandingAmount}
              error={
                showErrors &&
                !!errors[`loanOrCreditCard_${index}_outstandingAmount`]
              }
              onChange={(e) =>
                handleTableChange(
                  e,
                  index,
                  "outstandingAmount",
                  "outstandingLoandsOrCreditCardDetails",
                )
              }
            />
          </div>
        ))}

      {/* Household spend */}
      <div className="input-group-row">
        <div className="Content">
          What amount per month do you spend on household & utilities? (£)
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <InputField
            name="householdExpenditurePerMonth"
            type="number"
            placeholder="Amount"
            value={formData.householdExpenditurePerMonth}
            error={showErrors && !!errors.householdExpenditurePerMonth}
            onChange={handleChange}
          />
        </div>
      </div>

      {/* BTL (only if rental income) */}
      {formData.rentalIncome === true && (
        <>
          <div className="separator" />
          <div className="input-group-row">
            <div className="Content">
              Do you have a BTL mortgage?
              <span style={{ color: "var(--Error)" }}> *</span>
            </div>
            <div className="input-group">
              <YesNoRadio
                name="BTLMortage"
                value={formData.BTLMortage}
                error={errors.BTLMortage}
                onChange={(e) => handleYesNoChange(e, "BTLMortage")}
              />
              {formData.BTLMortage === true && (
                <div className="button-group">
                  <button
                    type="button"
                    className="icon-button add-button"
                    disabled={!formData.BTLMortage}
                    onClick={() => handleAddRow("BTLMortageDetails")}
                  >
                    <i className="fas fa-plus" />
                  </button>
                  <button
                    type="button"
                    className="icon-button delete-button"
                    disabled={!formData.BTLMortage}
                    onClick={() => handleRemoveRow("BTLMortageDetails")}
                  >
                    <i className="fas fa-trash" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {formData.BTLMortage === true &&
        formData.BTLMortageDetails.map((entry, index) => (
          <div key={index} className="input-group">
            <InputField
              name="lender"
              placeholder="Lender"
              value={entry.lender}
              error={
                showErrors && !!errors[`BTLMortageDetails_${index}_lender`]
              }
              onChange={(e) =>
                handleTableChange(e, index, "lender", "BTLMortageDetails")
              }
            />
            <InputField
              name="monthlyAmount"
              type="number"
              placeholder="Monthly Repayment Amount"
              value={entry.monthlyAmount}
              error={
                showErrors &&
                !!errors[`BTLMortageDetails_${index}_monthlyAmount`]
              }
              onChange={(e) =>
                handleTableChange(
                  e,
                  index,
                  "monthlyAmount",
                  "BTLMortageDetails",
                )
              }
            />
            <InputField
              name="outstandingAmount"
              type="number"
              placeholder="Total amount outstanding"
              value={entry.outstandingAmount}
              error={
                showErrors &&
                !!errors[`BTLMortageDetails_${index}_outstandingAmount`]
              }
              onChange={(e) =>
                handleTableChange(
                  e,
                  index,
                  "outstandingAmount",
                  "BTLMortageDetails",
                )
              }
            />
          </div>
        ))}

      {/* DMP */}
      <div className="separator" />
      <div className="input-group-row">
        <div className="Content">
          Are you currently in a debt management plan?
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="debtManagementPlan"
            value={formData.debtManagementPlan}
            error={errors.debtManagementPlan}
            onChange={(e) => handleYesNoChange(e, "debtManagementPlan")}
          />
          {formData.debtManagementPlan === true && (
            <div className="button-group">
              <button
                type="button"
                className="icon-button add-button"
                disabled={!formData.debtManagementPlan}
                onClick={() => handleAddRow("debtManagementPlanDetails")}
              >
                <i className="fas fa-plus" />
              </button>
              <button
                type="button"
                className="icon-button delete-button"
                disabled={!formData.debtManagementPlan}
                onClick={() => handleRemoveRow("debtManagementPlanDetails")}
              >
                <i className="fas fa-trash" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* <div className="sizebox" /> */}
      {formData.debtManagementPlan === true &&
        formData.debtManagementPlanDetails.map((entry, index) => (
          <div key={index} className="input-group">
            <InputField
              name="practitionerName"
              placeholder="Practitioner Name"
              value={entry.practitionerName}
              error={
                showErrors &&
                !!errors[`debtManagementPlanDetails_${index}_practitionerName`]
              }
              onChange={(e) =>
                handleTableChange(
                  e,
                  index,
                  "practitionerName",
                  "debtManagementPlanDetails",
                )
              }
            />
            <InputField
              name="monthlyAmount"
              type="number"
              placeholder="Monthly Repayment Amount"
              value={entry.monthlyAmount}
              error={
                showErrors &&
                !!errors[`debtManagementPlanDetails_${index}_monthlyAmount`]
              }
              onChange={(e) =>
                handleTableChange(
                  e,
                  index,
                  "monthlyAmount",
                  "debtManagementPlanDetails",
                )
              }
            />
            <InputField
              name="outstandingAmount"
              type="number"
              placeholder="Total amount outstanding"
              value={entry.outstandingAmount}
              error={
                showErrors &&
                !!errors[`debtManagementPlanDetails_${index}_outstandingAmount`]
              }
              onChange={(e) =>
                handleTableChange(
                  e,
                  index,
                  "outstandingAmount",
                  "debtManagementPlanDetails",
                )
              }
            />
          </div>
        ))}

      {/* FS Debt */}
      <div className="separator" />
      <div className="input-group-row">
        <div className="Content">
          Do you have any outstanding financial services debt?
          <span style={{ color: "var(--Error)" }}> *</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="outstandingFinancialServicesDebt"
            value={formData.outstandingFinancialServicesDebt}
            error={errors.outstandingFinancialServicesDebt}
            onChange={(e) =>
              handleYesNoChange(e, "outstandingFinancialServicesDebt")
            }
          />
          {formData.outstandingFinancialServicesDebt === true && (
            <div className="button-group">
              <button
                type="button"
                className="icon-button add-button"
                disabled={!formData.outstandingFinancialServicesDebt}
                onClick={() =>
                  handleAddRow("outstandingFinancialServicesDebtDetails")
                }
              >
                <i className="fas fa-plus" />
              </button>
              <button
                type="button"
                className="icon-button delete-button"
                disabled={!formData.outstandingFinancialServicesDebt}
                onClick={() =>
                  handleRemoveRow("outstandingFinancialServicesDebtDetails")
                }
              >
                <i className="fas fa-trash" />
              </button>
            </div>
          )}
        </div>
      </div>

      {formData.outstandingFinancialServicesDebt === true &&
        formData.outstandingFinancialServicesDebtDetails.map((entry, index) => (
          <div key={index} className="input-group">
            <div className="Content"></div>
            <div className="input-group">
              <InputField
                name="to"
                placeholder="To whom"
                value={entry.to}
                error={
                  showErrors &&
                  !!errors[
                    `outstandingFinancialServicesDebtDetails_${index}_to`
                  ]
                }
                onChange={(e) =>
                  handleTableChange(
                    e,
                    index,
                    "to",
                    "outstandingFinancialServicesDebtDetails",
                  )
                }
              />
              <InputField
                name="amount"
                type="number"
                placeholder="Amount"
                value={entry.amount}
                error={
                  showErrors &&
                  !!errors[
                    `outstandingFinancialServicesDebtDetails_${index}_amount`
                  ]
                }
                onChange={(e) =>
                  handleTableChange(
                    e,
                    index,
                    "amount",
                    "outstandingFinancialServicesDebtDetails",
                  )
                }
              />
            </div>
          </div>
        ))}

      <div className="separator" />
      <div className="input-group-row">
        <div className="Content">Total Assets</div>
        <div className="input-group">
          <InputField
            name="totalAssets"
            type="number"
            readOnly
            value={formData.totalAssets}
          />
        </div>
      </div>
      <div className="input-group-row">
        <div className="Content">Total Liabilities</div>
        <div className="input-group">
          <InputField
            name="totalLiabilities"
            type="number"
            readOnly
            value={formData.totalLiabilities}
          />
        </div>
      </div>

      {/* <div className="sizebox" /> */}
      {!isSubmitted && <PrimaryButton label="SAVE" onClick={handleSubmit} />}
    </form>
  );
};

export default Section8;
